import Vue from 'vue';
import api from '../../api/documentImport';
import rideInformationApi from '../../api/rideInformation';
import config from '../../common/config';
import { pollJobStatus } from '../../common/pollJobStatus';
import { emptyTripInfo, mergeTripInfo } from '../../utils/documentImportTripInfo';
import { uploadDocumentForImport, deleteStoredDocument } from '../../utils/documentImportStorage';
import { uploadsToDelete, VOUCHER_TYPES } from '../../utils/documentImportUploadCleanup';
import { uploadRejectionKey } from '../../utils/documentImportUploadLimits';
import {
  accommodationsMissingDates, endDateForDayCount, fromBackendDate, hasBothDates, toBackendDate,
} from '../../utils/documentImportDates';
import { router } from '../../main';
import editRideApi from '../../api/editRide';
import { attachParsedDocuments } from '../../services/attachParsedDocuments';
import { buildAttachOperations } from '../../utils/buildAttachOperations';
import { filterAttachOperationsByResult } from '../../utils/filterAttachOperationsByResult';
import {
  buildImportRidePayload,
  createdRidePath,
  resetImportRideSettings,
} from '../../utils/documentImportCreateRide';
import {
  mergeCreationErrors,
  dropCreationErrorsForResults,
  attemptedResultIndexes,
  remainingGeneralInfo,
  attachedResultUpdates,
} from '../../utils/attachRunBookkeeping';

const DOCUMENT_IMAGES = {
  importDocumentsFlightDeparture: config.config.DOCUMENT_IMPORT_FLIGHT_TAKEOFF_IMAGE,
  importDocumentsFlightArrival: config.config.DOCUMENT_IMPORT_FLIGHT_LANDING_IMAGE,
  car_rental: config.config.DOCUMENT_IMPORT_CAR_RENTAL_IMAGE,
  accommodation: config.config.DOCUMENT_IMPORT_ACCOMMODATION_IMAGE,
};

function dateRangeWarning(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const diff = (new Date(endDate) - new Date(startDate)) / (24 * 60 * 60 * 1000);
  return diff > 60 ? { start: startDate, end: endDate } : null;
}

function normalizeFlightPoints(data) {
  const points = [];
  const segments = data.segments || [];
  const multi = segments.length > 1;
  segments.forEach((seg, i) => {
    const conf = seg.booking_confirmation ? `\nConf: ${seg.booking_confirmation}` : '';
    const shared = `${seg.airline || ''} ${seg.flight_number || ''} · ${seg.departure_city} ${seg.departure_time || ''} → ${seg.arrival_city} ${seg.arrival_time || ''}${conf}`.trim();
    points.push({
      labelKey: 'importDocumentsFlightDeparture',
      labelSuffix: multi ? `${i + 1} · ${seg.departure_city}` : seg.departure_city,
      searchQuery: [seg.departure_name, seg.departure_city, seg.departure_country].filter(Boolean).join(' '),
      date: seg.departure_date || '',
      time: seg.departure_time || '',
      text: shared,
      place: null,
    });
    points.push({
      labelKey: 'importDocumentsFlightArrival',
      labelSuffix: multi ? `${i + 1} · ${seg.arrival_city}` : seg.arrival_city,
      searchQuery: [seg.arrival_name, seg.arrival_city, seg.arrival_country].filter(Boolean).join(' '),
      date: seg.arrival_date || '',
      time: seg.arrival_time || '',
      text: shared,
      place: null,
    });
  });
  return points;
}

function normalizeCarRentalPoints(data) {
  const conf = data.booking_confirmation ? `Conf: ${data.booking_confirmation}` : '';
  const shared = [
    data.company,
    data.car_type,
    data.pickup ? `Pickup: ${data.pickup.name}${data.pickup.address ? ', ' + data.pickup.address : ''} (${data.pickup.date} ${data.pickup.time})` : '',
    data.return ? `Return: ${data.return.name}${data.return.address ? ', ' + data.return.address : ''} (${data.return.date} ${data.return.time})` : '',
    conf,
  ].filter(Boolean).join('\n');

  return [
    {
      label: `Pickup car from ${(data.pickup && data.pickup.name) || (data.pickup && data.pickup.city) || ''}`,
      searchQuery: (data.pickup && data.pickup.name) || '',
      date: (data.pickup && data.pickup.date) || '',
      time: (data.pickup && data.pickup.time) || '',
      text: shared,
      place: null,
    },
    {
      label: `Return car to ${(data.return && data.return.name) || (data.return && data.return.city) || ''}`,
      searchQuery: (data.return && data.return.name) || '',
      date: (data.return && data.return.date) || '',
      time: (data.return && data.return.time) || '',
      text: shared,
      place: null,
    },
  ];
}

function normalizeAttractionPoint(data) {
  return [{
    label: data.name || '',
    searchQuery: [data.name_en || data.name, data.city_en || data.city, data.country_en || data.country].filter(Boolean).join(' '),
    date: data.date || '',
    time: data.time || '',
    text: data.text || '',
    place: null,
  }];
}

function normalizePoints(type, data) {
  if (type === 'flight') return normalizeFlightPoints(data);
  if (type === 'car_rental') return normalizeCarRentalPoints(data);
  if (type === 'attraction' || type === 'show') return normalizeAttractionPoint(data);
  if (type === 'itinerary') return normalizeItineraryPoints(data);
  return [];
}

function normalizeItineraryPoints(data) {
  return (data.items || []).map(item => {
    const hasAddress = !!item.address;
    const nameEn = item.name_en || item.name;
    const cityEn = item.city_en || item.city;
    const countryEn = item.country_en || item.country;
    return {
      label: item.name || '',
      searchQuery: hasAddress
        ? [nameEn, item.address].filter(Boolean).join(', ')
        : [nameEn, cityEn, countryEn].filter(Boolean).join(' '),
      searchQueryFallback: hasAddress
        ? [nameEn, cityEn, countryEn].filter(Boolean).join(' ')
        : '',
      date: item.date || '',
      // Set when the document numbers its days instead of dating them; the real
      // date is worked out from the ride's start when the ride is created.
      dayNumber: Number(item.day_number) || null,
      time: item.time || '',
      text: item.description || '',
      place: null,
    };
  });
}

/**
 * The highest day number any document mentioned.
 *
 * A booklet numbered "יום 1 / יום 2" states how long the trip is without ever
 * saying when it starts, so this is what lets the end date follow from the start.
 * 0 when nothing is numbered.
 */
function maxDayNumber(results) {
  let highest = 0;
  (results || []).forEach((result) => {
    if (!result || result.discarded || result.status !== 'done') return;
    (result.points || []).forEach((point) => {
      highest = Math.max(highest, Number(point.dayNumber) || 0);
    });
    const data = (result.extracted && result.extracted.data) || {};
    (data.day_notes || []).forEach((note) => {
      highest = Math.max(highest, Number(note.day_number) || 0);
    });
  });
  return highest;
}

const state = {
  results: [],
  rideName: '',
  rideStartDate: '',
  rideEndDate: '',
  rideSummary: '',
  generalInfo: [],
  isCreating: false,
  creationErrors: [],
  createdRideId: null,
  dateRangeWarning: null,
  suggestingRideName: false,
  targetRideId: null,
  // Which ride the staged cards were added for (null: the create flow). The
  // assistant on a ride page drops cards staged for any other ride.
  resultsRideId: null,
  // Bumped by every documentImportReset. Work that started before a reset --
  // an upload, a parse that can run for minutes, an attach -- compares it after
  // each await and writes nothing once it changed, so a manager who moved on to
  // another ride never gets the old ride's cards (results are addressed by
  // index, and the new ride's first card reuses index 0).
  generation: 0,
  ...resetImportRideSettings(),
};

const getters = {
  documentImportResults: state => state.results,
  documentImportRideName: state => state.rideName,
  documentImportRideLanguage: state => state.rideLanguage,
  documentImportIsPublic: state => state.isPublic,
  documentImportIsSubdomain: state => state.isSubdomain,
  documentImportRideStartDate: state => state.rideStartDate,
  documentImportRideEndDate: state => state.rideEndDate,
  documentImportIsCreating: state => state.isCreating,
  documentImportHasAnyResult: state => state.results.length > 0,
  documentImportConfirmedCount: state =>
    state.results.filter(r => r.status === 'done' && !r.discarded).length,
  documentImportIsProcessing: state =>
    state.results.some(r => r.status === 'processing'),
  documentImportProcessingCount: state =>
    state.results.filter(r => r.status === 'processing').length,
  documentImportAllDoneHavePlace: state =>
    state.results.every(r => {
      if (r.discarded || r.status !== 'done') return true;
      if (r.type === 'accommodation') return r.place !== null;
      return (r.points || []).every(p => p.place !== null);
    }),
  documentImportDoubleBookingConflicts: state => {
    const confirmed = state.results.filter(
      r => r.status === 'done' && !r.discarded && r.type === 'accommodation' && r.extracted && r.extracted.data
    );
    const conflicts = [];
    for (let i = 0; i < confirmed.length; i++) {
      for (let j = i + 1; j < confirmed.length; j++) {
        const a = confirmed[i].extracted.data;
        const b = confirmed[j].extracted.data;
        if (a.check_in_date < b.check_out_date && b.check_in_date < a.check_out_date) {
          conflicts.push({ nameA: a.name, nameB: b.name });
        }
      }
    }
    return conflicts;
  },
  documentImportCreationErrors: state => state.creationErrors,
  documentImportCreatedRideId: state => state.createdRideId,
  documentImportSuggestingRideName: state => state.suggestingRideName,
  documentImportRideSummary: state => state.rideSummary,
  // A document need not state any of these; without them the ride cannot be
  // created, so the screen asks for them rather than submitting empty values.
  documentImportHasRideDates: state => hasBothDates(state.rideStartDate, state.rideEndDate),
  documentImportHasRideName: state => !!(state.rideName || '').trim(),
  documentImportAccommodationsMissingDates: state => accommodationsMissingDates(state.results),
  // The highest day number any document mentioned, so a numbered itinerary can
  // suggest its own end date once the traveller picks a start.
  documentImportDayCount: state => maxDayNumber(state.results),
  documentImportGeneralInfo: state => state.generalInfo,
  documentImportDateRangeWarning: state => state.dateRangeWarning,
  documentImportResultsRideId: state => state.resultsRideId,
};

async function _processDocumentJob({ commit, state, dispatch }, { index, apptoken, jobId, file, uploadToStorage, generation }) {
  // Must outlast DOCUMENT_PARSE_TIMEOUT on the server (1000s): a large itinerary
  // carrying the document's own text for every place can generate for minutes,
  // and giving up here would show an error for a job that is still running.
  const { status, result: extracted } = await pollJobStatus(
    () => api.getJobStatus(apptoken, jobId),
    { timeout: 18 * 60 * 1000 },
  );
  // Reset while this was being read: every commit below would land on cards
  // that now belong to someone else's run.
  if (state.generation !== generation) return;

  if (status !== 'complete') {
    commit('documentImportUpdateResult', { index, status: 'error', extracted: null, type: null, points: [] });
    return;
  }

  if (!extracted.success) {
    const status = extracted.error === 'not_accommodation_document' ? 'not_accommodation' : 'error';
    commit('documentImportUpdateResult', { index, status, extracted: null, type: null, points: [] });
    return;
  }

  const type = extracted.type || 'unknown';
  if (type === 'unknown') {
    commit('documentImportUpdateResult', { index, status: 'not_accommodation', extracted: null, type: 'unknown', points: [] });
    return;
  }

  commit('documentImportUpdateResult', { index, status: 'done', extracted, type, points: normalizePoints(type, extracted.data) });

  if (type === 'itinerary') {
    commit('documentImportMergeTripInfo', extracted.data);

    for (const emb of (extracted.data.embedded || [])) {
      const embType = emb.type;
      const embExtracted = { success: true, type: embType, data: emb.data, ride: emb.ride || extracted.ride, confidence: extracted.confidence, missing_fields: [] };
      // No uploadPath: an embedded card shares the itinerary's file but does
      // not own it, so the sweep must never consider it the file's keeper.
      commit('documentImportAddResult', { file, status: 'done', processingStartedAt: null, type: embType, extracted: embExtracted, points: normalizePoints(embType, emb.data), error: null, editing: false, discarded: false, place: null, voucherUrl: null, uploadUrl: null, uploadPath: null });
      commit('documentImportMergeRideDates', { startDate: emb.ride && emb.ride.start_date, endDate: emb.ride && emb.ride.end_date, suggestedName: null });
    }
  }

  // The file is already in storage -- it was uploaded before the parse, so the
  // server could be sent a URL instead of the bytes. What is left to decide is
  // whether it stays there. A voucher type keeps it: that URL is saved on the
  // trip and is what the traveller opens. An itinerary was read and is done
  // with, so the sweep below removes it.
  const uploaded = state.results[index] || {};
  if (uploadToStorage && uploaded.uploadUrl && VOUCHER_TYPES.indexOf(type) !== -1) {
    commit('documentImportSetVoucherUrl', { index, url: uploaded.uploadUrl });
  }

  commit('documentImportMergeRideDates', {
    startDate: extracted.ride.start_date,
    endDate: extracted.ride.end_date,
    suggestedName: extracted.ride.suggested_name,
  });

  // A ride being attached to already has a name; only the creation flow needs
  // one suggested.
  if (!state.targetRideId && state.results.every(r => r.status !== 'processing')) {
    dispatch('documentImportSuggestRideName');
  }
}

const actions = {
  async documentImportUploadFile({ commit, state, rootState, dispatch }, file) {
    const index = state.results.length;
    const generation = state.generation;
    const { apptoken } = rootState.appauth;
    const lang = rootState.language && rootState.language.currentLanguage === 'en' ? 'english' : 'hebrew';
    // Checked before the upload starts: a 10MB cap the user only discovers
    // part-way through the worker's download is a slow way to say no. `error`
    // carries the translation key the card shows.
    const rejection = uploadRejectionKey(file);
    if (rejection) {
      commit('documentImportAddResult', { file, status: 'error', processingStartedAt: null, type: null, extracted: null, points: [], error: rejection, editing: false, discarded: false, place: null, voucherUrl: null, uploadUrl: null, uploadPath: null });
      return;
    }

    commit('documentImportAddResult', { file, status: 'processing', processingStartedAt: Date.now(), type: null, extracted: null, points: [], error: null, editing: false, discarded: false, place: null, voucherUrl: null, uploadUrl: null, uploadPath: null });
    try {
      // Storage first, then parse from the URL: the document never travels
      // through our API or through Redis. A voucher needed this upload anyway,
      // it just used to happen after the parse instead of before it.
      const { url, path } = await uploadDocumentForImport(file);
      // Reset during the upload: no card will ever record this file, so the
      // sweep could never find it -- remove it here.
      if (state.generation !== generation) {
        await deleteStoredDocument(path);
        return;
      }
      commit('documentImportSetUpload', { index, url, path });

      const { data } = await api.parseDocument(apptoken, { url, mimeType: file.type, language: lang });
      if (state.generation !== generation) return;
      if (!data.job_id) {
        commit('documentImportUpdateResult', { index, status: 'error', extracted: null, type: null, points: [] });
        return;
      }
      await _processDocumentJob({ commit, state, dispatch }, { index, apptoken, jobId: data.job_id, file, uploadToStorage: true, generation });
    } catch {
      if (state.generation === generation) {
        commit('documentImportUpdateResult', { index, status: 'error', extracted: null, type: null, points: [] });
      }
    } finally {
      // Every way out of this -- a failed upload, a refused URL, a job that
      // never finished, a document that was not one of ours, or an itinerary
      // we have finished reading -- leaves a file to remove. The sweep keeps
      // anything a card still needs, so the voucher cases fall through it.
      await dispatch('documentImportCleanupUploads');
    }
  },

  /**
   * Delete the uploaded documents nothing will reference.
   *
   * Which ones those are is decided by documentImportUploadCleanup, which
   * keeps a file unless it can show no card needs it -- a voucher is the
   * traveller's own document and deleting one cannot be undone.
   */
  async documentImportCleanupUploads({ commit, state }) {
    for (const { index, path } of uploadsToDelete(state.results)) {
      const removed = await deleteStoredDocument(path);
      if (removed) commit('documentImportClearUpload', index);
    }
  },

  /** Discard a card and clean up the document it no longer needs. */
  async documentImportDiscardResultAndCleanup({ commit, dispatch }, index) {
    commit('documentImportDiscardResult', index);
    await dispatch('documentImportCleanupUploads');
  },

  async documentImportSubmitText({ commit, state, rootState, dispatch }, text) {
    const index = state.results.length;
    const generation = state.generation;
    const file = { name: 'Text input' };
    const { apptoken } = rootState.appauth;
    const lang = rootState.language && rootState.language.currentLanguage === 'en' ? 'english' : 'hebrew';
    commit('documentImportAddResult', { file, status: 'processing', processingStartedAt: Date.now(), type: null, extracted: null, points: [], error: null, editing: false, discarded: false, place: null, voucherUrl: null, uploadUrl: null, uploadPath: null });
    try {
      const { data } = await api.parseText(apptoken, text, lang);
      if (state.generation !== generation) return;
      if (!data.job_id) { commit('documentImportUpdateResult', { index, status: 'error', extracted: null, type: null, points: [] }); return; }
      await _processDocumentJob({ commit, state, dispatch }, { index, apptoken, jobId: data.job_id, file, uploadToStorage: false, generation });
    } catch {
      if (state.generation === generation) {
        commit('documentImportUpdateResult', { index, status: 'error', extracted: null, type: null, points: [] });
      }
    }
  },

  async documentImportSuggestRideName({ commit, state, rootState }) {
    const { apptoken } = rootState.appauth;
    const lang = rootState.language && rootState.language.currentLanguage === 'en' ? 'english' : 'hebrew';

    const documents = state.results
      .filter(r => r.status === 'done' && r.extracted)
      .map(r => {
        const base = { type: r.type, family_name: r.extracted.data.family_name || '' };
        if (r.type === 'flight') {
          return { ...base, segments: (r.extracted.data.segments || []).map(s => ({ departure_city: s.departure_city, arrival_city: s.arrival_city })) };
        }
        if (r.type === 'accommodation') return { ...base, city: r.extracted.data.city || '' };
        if (r.type === 'car_rental') return { ...base, pickup_city: (r.extracted.data.pickup || {}).city || '', return_city: (r.extracted.data.return || {}).city || '' };
        if (r.type === 'attraction' || r.type === 'show') return { ...base, city: r.extracted.data.city || '', name: r.extracted.data.name || '' };
        if (r.type === 'itinerary') return { ...base, cities: [...new Set((r.extracted.data.items || []).map(i => i.city).filter(Boolean))] };
        return base;
      });

    commit('documentImportSetSuggestingRideName', true);
    try {
      const submitResponse = await api.suggestRideName(apptoken, documents, lang);
      const jobId = submitResponse.data.job_id;
      if (!jobId) return;

      const { status, result } = await pollJobStatus(
        () => api.getJobStatus(apptoken, jobId),
        { timeout: 2 * 60 * 1000 },
      );
      if (status === 'complete' && result) commit('documentImportSetRideName', result);
    } catch (_) {
      // non-fatal — keep existing name
    } finally {
      commit('documentImportSetSuggestingRideName', false);
    }
  },

  async documentImportCreateRide({ commit, state, rootState, dispatch }, { t, isFirstRide } = {}) {
    // The button is disabled without these, but this is what actually posts.
    if (!hasBothDates(state.rideStartDate, state.rideEndDate)) return;
    if (!(state.rideName || '').trim()) return;
    if (accommodationsMissingDates(state.results).length > 0) return;
    commit('documentImportSetCreating', true);
    const { apptoken } = rootState.appauth;

    try {
      // Step 1: create the ride using the existing endpoint
      const rideResponse = await rideInformationApi.setRideInformation(apptoken, buildImportRidePayload({
        rideName: state.rideName,
        rideStartDate: state.rideStartDate,
        rideEndDate: state.rideEndDate,
        rideSummary: state.rideSummary,
        rideLanguage: state.rideLanguage,
        isPublic: state.isPublic,
        isSubdomain: state.isSubdomain,
      }));
      const rideId = rideResponse.data.rideId;
      commit('documentImportSetCreatedRideId', rideId);

      const operations = buildAttachOperations({
        results: state.results,
        generalInfo: state.generalInfo,
        rideStartDate: state.rideStartDate,
        t,
        images: DOCUMENT_IMAGES,
      });
      const { errors } = await attachParsedDocuments({ apptoken, rideId, operations });

      if (errors.length > 0) {
        commit('documentImportSetCreationErrors', errors);
      } else {
        commit('documentImportReset');
        router.push(createdRidePath(rideId, isFirstRide));
      }
    } catch {
      dispatch('showModalWithParameters', {
        errorNumber: 'general_error',
        type: 'textConverter',
      });
    } finally {
      commit('documentImportSetCreating', false);
    }
  },

  async documentImportExtendRideDates({ rootState, dispatch }, { newStart, newEnd }) {
    const rideId = rootState.documentImport.targetRideId;
    const { apptoken } = rootState.appauth;
    try {
      const response = await editRideApi.updateRideDuration(apptoken, rideId, {
        rideCheckin: toBackendDate(newStart),
        rideCheckout: toBackendDate(newEnd),
      });
      // The server spells success "secsses"; the 60-day cap answers
      // "limiting_the_trip_duration".
      if (response.data.ServerMassageCode !== 'secsses') {
        dispatch('showModalWithParameters', {
          errorNumber: response.data.ServerMassageCode,
          type: 'textConverter',
        });
        return false;
      }
      await dispatch('fetchRide');
      return true;
    } catch {
      dispatch('showModalWithParameters', {
        errorNumber: 'general_error',
        type: 'textConverter',
      });
      return false;
    }
  },

  /**
   * Attaches parsed documents to the ride already on the server.
   *
   * `resultIndexes`, when given, scopes the run to those documents only --
   * what a single card's own "add this document" button sends, and what the
   * bulk button now sends once it stops being all-or-nothing (it passes every
   * card that is actually ready, which may be fewer than everything staged).
   * Omitted, every active document is sent, unchanged from before this
   * existed.
   *
   * The trip-level sections (resultIndex: null) are never filtered out by
   * scoping: they belong to no single card and are merged across every
   * itinerary document uploaded, so they cannot be split per card. Whichever
   * attach call runs first -- one card or a batch -- is the one that gets to
   * post them, and documentImportSettleGeneralInfo right after makes sure no
   * later attach, scoped or not, repeats the sections that landed -- while
   * keeping any the server rejected, which no card would otherwise remember.
   */
  async documentImportAttachToRide({
    commit, state, rootState, dispatch, getters,
  }, { t, resultIndexes } = {}) {
    const rideId = state.targetRideId;
    if (!rideId) return;
    // Two uploaded accommodation vouchers cannot both be attached when their
    // stays overlap -- the server rejects the second one and leaves the
    // traveller at a dead-end error popup instead of a way to fix it here.
    // This guards every attach, scoped or not: a single card can never be the
    // half of a pair that still collides with its sibling.
    if (getters.documentImportDoubleBookingConflicts.length > 0) return;
    const generation = state.generation;
    commit('documentImportSetCreating', true);
    const { apptoken } = rootState.appauth;
    try {
      const operations = buildAttachOperations({
        results: state.results,
        generalInfo: state.generalInfo,
        // Day-numbered documents resolve against the start date of the ride
        // already on the server -- NOT state.rideStartDate, which
        // documentImportMergeRideDates derives from the uploaded documents
        // themselves and can be empty (a pure day-numbered booklet) or land
        // outside this trip entirely (a document carrying its own dates).
        // The ride module keeps the fetched ride at rootState.ride.ride.ride
        // (see setRide in store/modules/ride.js and allRide.ride.start_date
        // in AiChatPanel.vue), serialized DD-MM-YYYY by the backend.
        rideStartDate: fromBackendDate(rootState.ride?.ride?.ride?.start_date),
        t,
        images: DOCUMENT_IMAGES,
      });
      const scopedOperations = resultIndexes
        ? filterAttachOperationsByResult(operations, resultIndexes)
        : operations;
      const {
        errors, extraInfoErrors, succeededResultIndexes, failedGeneralInfoSections,
      } = await attachParsedDocuments({
        apptoken, rideId, operations: scopedOperations,
      });
      // The manager moved to another ride while this was posting. It landed on
      // the ride it was meant for; the indexes below now point at the other
      // ride's cards, and nothing here may be reported as added there.
      if (state.generation !== generation) return false;
      // Day notes and general-information sections were historically silent.
      // Silence is what let the panel report "1 document added" over notes that
      // never reached the trip, so here -- unlike in the create flow, which
      // still reads `errors` alone -- they are reported like any other failure.
      const runErrors = errors.concat(extraInfoErrors);
      // The button (bulk or per-card) stays live so the manager can fix a
      // rejected card and try again -- which means everything that DID land
      // has to be taken out of the run first, or the retry creates it on the
      // ride a second time.
      commit('documentImportMarkResultsAttached', succeededResultIndexes);
      // The trip-level sections belong to no card, so dropping one that was
      // rejected would destroy it outright. Only the ones that actually landed
      // are retired; the rest stay staged for the next attach.
      if (scopedOperations.some(op => op.resultIndex === null)) {
        commit('documentImportSettleGeneralInfo', failedGeneralInfoSections);
      }
      // Accumulated, not replaced: a card that failed earlier keeps its line on
      // screen while an unrelated card is attached, and loses it only when it
      // is itself retried, attached, or taken off the panel.
      commit('documentImportMergeCreationErrors', {
        errors: runErrors,
        attempted: attemptedResultIndexes(scopedOperations),
      });
      // Nothing left staged and nothing outstanding -- safe to clear the whole
      // panel back to its empty state. A card that is still processing or
      // still blocked (never part of this run, or part of a partial batch)
      // keeps this from firing, so it is never swept away mid-flight. The test
      // is the accumulated list, not just this run's: an older card's failure
      // is reason enough to keep the panel as it is.
      if (state.creationErrors.length === 0 && !state.results.some(r => r && !r.discarded)) {
        commit('documentImportReset');
      }
      await dispatch('fetchRide');
      await dispatch('fetchAccommodations');
      return runErrors.length === 0;
    } catch {
      dispatch('showModalWithParameters', { errorNumber: 'general_error', type: 'textConverter' });
      return false;
    } finally {
      commit('documentImportSetCreating', false);
    }
  },
};

const mutations = {
  documentImportAddResult(state, result) {
    if (!state.results.length) state.resultsRideId = state.targetRideId;
    state.results.push(result);
  },

  // The three index-based writes below ignore an index that no longer exists:
  // an upload still being read when the assistant moves to another ride lands
  // after documentImportReset, and Vue.set would otherwise create a ghost card
  // on the new ride.
  documentImportUpdateResult(state, { index, status, extracted, type, points }) {
    if (!state.results[index]) return;
    const updated = { ...state.results[index], status, extracted };
    if (type !== undefined) updated.type = type;
    if (points !== undefined) updated.points = points;
    Vue.set(state.results, index, updated);
  },

  documentImportSetPlace(state, { index, place }) {
    Vue.set(state.results, index, { ...state.results[index], place });
  },

  documentImportSetVoucherUrl(state, { index, url }) {
    if (!state.results[index]) return;
    Vue.set(state.results, index, { ...state.results[index], voucherUrl: url });
  },

  documentImportSetPointPlace(state, { resultIndex, pointIndex, place }) {
    const result = state.results[resultIndex];
    const newPoints = (result.points || []).map((p, i) => (i === pointIndex ? { ...p, place } : p));
    Vue.set(state.results, resultIndex, { ...result, points: newPoints });
  },

  documentImportSetEditing(state, { index, editing }) {
    Vue.set(state.results, index, { ...state.results[index], editing });
  },

  documentImportSetUpload(state, { index, url, path }) {
    if (!state.results[index]) return;
    Vue.set(state.results, index, { ...state.results[index], uploadUrl: url, uploadPath: path });
  },

  /** Forget a file we have deleted, so the sweep cannot try it twice. */
  documentImportClearUpload(state, index) {
    const result = state.results[index];
    if (!result) return;
    Vue.set(state.results, index, { ...result, uploadPath: null });
  },

  documentImportDiscardResult(state, index) {
    Vue.set(state.results, index, { ...state.results[index], discarded: true });
    // Acting on the failure by throwing the document away is still acting on
    // it -- the complaint goes with the card.
    state.creationErrors = dropCreationErrorsForResults(state.creationErrors, [index]);
  },

  /**
   * Retires the documents that went onto the ride in full after a partial
   * attach failure.
   *
   * `discarded` is what takes a result out of buildAttachOperations (isActive)
   * and off the panel, so a retry cannot post it twice; `attached` says WHY it
   * left, so the panel can account for the cards that vanished instead of
   * letting them disappear without a word.
   */
  documentImportMarkResultsAttached(state, indexes) {
    attachedResultUpdates(state.results, indexes).forEach(({ index, result }) => {
      Vue.set(state.results, index, result);
    });
  },

  /**
   * Only the attach flow calls this, once its sections have had their attempt.
   * The creation flow never does: a retry there builds a whole second ride,
   * which needs its own copy of them.
   *
   * Takes the positions of the sections the server REJECTED and keeps exactly
   * those. Clearing the lot win-or-lose is what used to lose a rejected
   * section for good -- it belongs to no card, so nothing else was holding it.
   */
  documentImportSettleGeneralInfo(state, failedSectionIndexes) {
    state.generalInfo = remainingGeneralInfo(state.generalInfo, failedSectionIndexes);
  },

  documentImportRemovePoint(state, { resultIndex, pointIndex }) {
    const result = state.results[resultIndex];
    const newPoints = (result.points || []).filter((_, i) => i !== pointIndex);
    const updated = { ...result, points: newPoints };
    if (newPoints.length === 0) {
      updated.discarded = true;
      // Emptying a card takes it off the panel; its failure line would be left
      // pointing at nothing.
      state.creationErrors = dropCreationErrorsForResults(state.creationErrors, [resultIndex]);
    }
    Vue.set(state.results, resultIndex, updated);
  },

  documentImportUpdateField(state, { index, field, value }) {
    const result = state.results[index];
    Vue.set(result.extracted.data, field, value);
  },

  documentImportRecalculateDates(state) {
    const active = state.results.filter(r => r.status === 'done' && !r.discarded && r.extracted);
    if (!active.length) return;
    const starts = active.map(r => {
      if (r.type === 'accommodation') return r.extracted.data && r.extracted.data.check_in_date;
      return r.extracted.ride && r.extracted.ride.start_date;
    }).filter(Boolean);
    const ends = active.map(r => {
      if (r.type === 'accommodation') return r.extracted.data && r.extracted.data.check_out_date;
      return r.extracted.ride && r.extracted.ride.end_date;
    }).filter(Boolean);
    if (!starts.length || !ends.length) return;
    const newStart = starts.reduce((min, d) => (d < min ? d : min));
    const newEnd = ends.reduce((max, d) => (d > max ? d : max));
    const warning = dateRangeWarning(newStart, newEnd);
    if (warning) {
      state.dateRangeWarning = warning;
      return;
    }
    state.dateRangeWarning = null;
    state.rideStartDate = newStart;
    state.rideEndDate = newEnd;
  },

  documentImportMergeTripInfo(state, data) {
    const merged = mergeTripInfo(
      { rideSummary: state.rideSummary, generalInfo: state.generalInfo },
      data,
    );
    state.rideSummary = merged.rideSummary;
    state.generalInfo = merged.generalInfo;
  },

  documentImportSetRideName(state, name) {
    state.rideName = name;
  },

  documentImportSetSuggestingRideName(state, val) {
    state.suggestingRideName = val;
  },

  documentImportSetRideStartDate(state, date) {
    state.rideStartDate = date;
    // A numbered itinerary knows how long it is but not when it starts. Once the
    // traveller says when, the end follows -- only filled in when they have not
    // chosen one themselves.
    if (!state.rideEndDate) {
      const suggested = endDateForDayCount(date, maxDayNumber(state.results));
      if (suggested) state.rideEndDate = suggested;
    }
    state.dateRangeWarning = dateRangeWarning(state.rideStartDate, state.rideEndDate);
  },

  documentImportSetRideEndDate(state, date) {
    state.rideEndDate = date;
    state.dateRangeWarning = dateRangeWarning(state.rideStartDate, state.rideEndDate);
  },

  documentImportMergeRideDates(state, { startDate, endDate, suggestedName }) {
    if (!state.rideName && suggestedName) {
      state.rideName = suggestedName;
    }

    const newStart = (!state.rideStartDate || startDate < state.rideStartDate)
      ? startDate : state.rideStartDate;
    const newEnd = (!state.rideEndDate || endDate > state.rideEndDate)
      ? endDate : state.rideEndDate;

    const warning = dateRangeWarning(newStart, newEnd);
    if (warning) {
      state.dateRangeWarning = warning;
      return;
    }

    state.dateRangeWarning = null;
    state.rideStartDate = newStart;
    state.rideEndDate = newEnd;
  },

  documentImportSetCreating(state, val) {
    state.isCreating = val;
  },

  // The create flow's own reporting: one run, one ride, one list.
  documentImportSetCreationErrors(state, errors) {
    state.creationErrors = errors;
  },

  /**
   * The attach flow's reporting. Several runs land on the same trip, so the
   * list accumulates instead of being replaced -- see mergeCreationErrors for
   * what retires an entry.
   */
  documentImportMergeCreationErrors(state, { errors, attempted }) {
    state.creationErrors = mergeCreationErrors(state.creationErrors, errors, attempted);
  },

  documentImportSetRideLanguage(state, value) {
    state.rideLanguage = value;
  },
  documentImportSetIsPublic(state, value) {
    state.isPublic = value;
  },
  documentImportSetIsSubdomain(state, value) {
    state.isSubdomain = value;
  },
  documentImportApplyRideSettings(state, { rideLanguage, isPublic, isSubdomain }) {
    state.rideLanguage = rideLanguage;
    state.isPublic = isPublic;
    state.isSubdomain = isSubdomain;
  },

  documentImportSetCreatedRideId(state, id) {
    state.createdRideId = id;
  },

  documentImportSetTargetRideId(state, rideId) {
    state.targetRideId = rideId;
  },

  documentImportReset(state) {
    state.results = [];
    state.rideName = '';
    state.rideStartDate = '';
    state.rideEndDate = '';
    state.rideSummary = emptyTripInfo().rideSummary;
    state.generalInfo = emptyTripInfo().generalInfo;
    state.isCreating = false;
    state.creationErrors = [];
    state.createdRideId = null;
    state.dateRangeWarning = null;
    state.targetRideId = null;
    state.resultsRideId = null;
    state.generation += 1;
    Object.assign(state, resetImportRideSettings());
  },
};

export default { state, getters, actions, mutations };
